import React from 'react';
import ResizeableSidebar from '@/components/ResizeableSidebar';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  resetWidget,
  selectChartType,
  selectDataTable,
  selectedChartWidget,
  selectIsLoading,
  selectIsWidgetConfigurationSidebarOpen,
  selectWidgetView,
  toggleWidgetConfigurationSidebar,
  setDimensions,
  setMetrics,
  selectDimensions,
  selectMetrics,
} from '@/features/dashboard/dashboardSlice';
import { ChartTitle } from '@/features/dashboard/components/ChartTitle';
import AjaxSpinner from '@/components/AjaxSpinner';
import { TrevorIcon } from '@trevor/ui-components';
import styled from 'styled-components';
import { REMOVE_PREVIEW_WIDGET } from '@/actions/constants';
import { MetricsConfiguration } from '@/features/chart-configuration/components/MetricsConfiguration';
import { DimensionsConfiguration } from '@/features/chart-configuration/components/DimensionsConfiguration';
import {
  useGetAvailableChartTypes,
  useGetPossibleMetricsAndDimensionsForChartType,
} from '@/features/chart-configuration/hooks';
import { getMetricsAndDimensionsForLegacyWidgets } from '@/features/dashboard/utils';
import { TABLE } from '@/components/charting/chartType.constants';
import { useMatch } from 'react-router-dom';
import { DASHBOARD_URL_PATH } from '@/routing/paths';
import { useGetSavedQuerySavedAggregateConfigQuery } from '@/features/dashboard/apiSlice';
import { useDatasourceId } from '@/hooks';
import ErrorBoundary from '@/components/ErrorBoundary';
import { DashboardWidgetChartConfiguration } from './DashboardWidgetChartConfiguration';
import { ConfigurationSectionTitle } from './ConfigurationSectionTitle';
import { SavedQuerySelect, SelectedSavedQuery } from './SavedQuerySelect';
import { SidebarContainer } from './WidgetConfigurationSidebar.styles';
import { FiltersConfiguration } from './FiltersConfiguration';
import { SwitchChartType } from './SwitchChartType';
import styles from './WidgetConfigurationSidebar.module.scss';

const CloseButtonContainer = styled.div`
  display: flex;
  justify-content: end;
  > svg {
    cursor: pointer;
  }
`;

export const WidgetConfigurationSidebar = () => {
  const dispatch = useAppDispatch();
  const datasourceId = useDatasourceId();
  const isShown = !!useMatch(DASHBOARD_URL_PATH);
  const isVisible = useAppSelector(selectIsWidgetConfigurationSidebarOpen);
  const widgetView = useAppSelector(selectWidgetView);
  const chartType = useAppSelector(selectChartType);
  const dataTable = useAppSelector(selectDataTable);
  const { isFetching } = useGetAvailableChartTypes(
    dataTable ? widgetView?._series : null,
  );
  const isLoading = useAppSelector(selectIsLoading);
  const widget = useAppSelector(selectedChartWidget);
  const selectedDimensions = useAppSelector(selectDimensions);
  const selectedMetrics = useAppSelector(selectMetrics);
  const { dimensionColumnsCombinations, metricColumns, prefill } =
    useGetPossibleMetricsAndDimensionsForChartType(
      dataTable ? widgetView?._series : null,
      chartType,
    );
  const { isFetching: isSavedConfigFetching, data: savedAggregateConfigs } =
    useGetSavedQuerySavedAggregateConfigQuery(
      {
        datasourceId,
        seriesId: widgetView?._series,
      },
      { skip: !widgetView?._series },
    );

  const handleCancel = () => {
    dispatch(toggleWidgetConfigurationSidebar());
    dispatch({ type: REMOVE_PREVIEW_WIDGET });
  };

  if (!isShown) return null;

  const handleResetState = () => dispatch(resetWidget());

  let legacyConfig;
  if (
    widget &&
    !widget?.metricColumns &&
    !widget?.dimensionColumns &&
    widget?.displayType !== TABLE &&
    widget?.displayType !== 'preview'
  ) {
    legacyConfig = getMetricsAndDimensionsForLegacyWidgets(
      dataTable,
      chartType,
    );
  }

  const isLegacyStacked =
    widget?.displayType === 'pie' && dataTable?.headings().length === 3;
  const prefilledConfig = savedAggregateConfigs
    ? savedAggregateConfigs[chartType]
    : prefill;

  return (
    <ResizeableSidebar
      onCloseDone={handleResetState}
      initialWidth={430}
      isVisible={isVisible}
      className={styles.sidebar}
      isRightSidebar
    >
      <SidebarContainer>
        <CloseButtonContainer>
          <TrevorIcon name="close" onClick={handleCancel} />
        </CloseButtonContainer>
        <ConfigurationSectionTitle>
          Saved query to display
        </ConfigurationSectionTitle>
        <ErrorBoundary>
          {!widgetView && <SavedQuerySelect />}

          {widgetView && <SelectedSavedQuery widgetView={widgetView} />}

          {(isLoading || isFetching || !dataTable || isSavedConfigFetching) && (
            <AjaxSpinner className={styles.loadingSpinner} />
          )}

          {!isLoading && !isFetching && dataTable && widgetView && (
            <>
              <FiltersConfiguration view={widgetView} />
              <ConfigurationSectionTitle>
                Configure chart
              </ConfigurationSectionTitle>
              <ChartTitle />
              <SwitchChartType />
              {chartType !== TABLE && dataTable && (
                <>
                  <MetricsConfiguration
                    chartType={chartType}
                    onChange={(metrics) => {
                      dispatch(setMetrics(metrics));
                    }}
                    widgetMetrics={
                      widget?.metricColumns || legacyConfig?.metrics
                    }
                    widgetChartType={
                      widget?.displayType
                        ? isLegacyStacked
                          ? 'stacked'
                          : widget.displayType
                        : null
                    }
                    dimensions={selectedDimensions}
                    metricColumns={metricColumns}
                    prefill={prefilledConfig?.metrics}
                  />
                  <DimensionsConfiguration
                    chartType={chartType}
                    onChange={(dimensions) => {
                      dispatch(setDimensions(dimensions));
                    }}
                    widgetDimensions={
                      widget?.dimensionColumns || legacyConfig?.dimensions
                    }
                    widgetChartType={
                      widget?.displayType
                        ? isLegacyStacked
                          ? 'stacked'
                          : widget.displayType
                        : null
                    }
                    metrics={selectedMetrics}
                    dimensionColumnsCombinations={dimensionColumnsCombinations}
                    prefill={prefilledConfig?.dimensions}
                  />
                </>
              )}
              <DashboardWidgetChartConfiguration />
            </>
          )}
        </ErrorBoundary>
      </SidebarContainer>
    </ResizeableSidebar>
  );
};
